import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const LOG_ROWS = [
  { time: '09:14', event: 'Pedido #2041', status: 'registrado' },
  { time: '09:14', event: 'Stock y margen', status: 'actualizado' },
  { time: '09:15', event: 'Excepción de entrega', status: 'trazada' },
  { time: '09:15', event: 'Visibilidad del día', status: 'completa' }
];

const CHAOS_TAGS = ['Está en el Excel', 'Lo hablamos por WhatsApp', 'Juan sabe cómo se hace', 'No quedó registrado'];

const PROCESS_STEPS = [
  { title: 'Nos conocemos', desc: 'Reunión para entender tu negocio y tu idea.' },
  { title: 'Propuesta', desc: 'Presentamos alcance, propuesta y presupuesto.' },
  { title: 'Refinamos', desc: 'Ajustamos según tus comentarios, sin letra chica.' },
  { title: 'Aceptás', desc: 'Das el ok. Arrancamos con claridad de alcance.' },
  { title: 'Trabajamos', desc: 'Creamos el grupo de WhatsApp y empezamos.' },
  { title: 'Entrega', desc: 'Recibís el sistema listo para operar.' }
];

const ease = [0.22, 1, 0.36, 1];

const chapterMotion = (reduceMotion, delay = 0) =>
  reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 22 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.28 },
        transition: { duration: 0.55, delay, ease }
      };

const DevLandingStory = () => {
  const reduceMotion = useReducedMotion();

  return (
    <section className="dl-story" aria-label="Por qué software a medida">
      <motion.article className="dl-chapter" {...chapterMotion(reduceMotion)}>
        <header className="dl-chapter__head">
          <span className="dl-chapter__index" aria-hidden="true">
            01
          </span>
          <h2 className="dl-chapter__title">Tu negocio y el desorden</h2>
        </header>

        <p className="dl-chapter__body">
          A medida que tu negocio escala, los procesos{' '}
          <strong>pierden visibilidad</strong>. Pedidos, excepciones y decisiones
          quedan en la memoria de las personas — o en un chat que nadie vuelve a
          leer.
        </p>
        <p className="dl-chapter__punch">Esto no se soluciona con más capacitación.</p>
        <p className="dl-chapter__body">
          Se soluciona con <strong>software a medida de tus procesos</strong>.
          Herramientas que no fallan ni olvidan. Registros para{' '}
          <strong>ver absolutamente todo</strong>.
        </p>

        <div className="dl-disorder" aria-hidden="true">
          <p className="dl-visual-label">Hoy</p>
          <div className="dl-tags">
            {CHAOS_TAGS.map((tag) => (
              <span key={tag} className="dl-tag">
                {tag}
              </span>
            ))}
          </div>

          <div className="dl-disorder__join" />

          <p className="dl-visual-label">Con software a medida</p>
          <div className="dl-log">
            <div className="dl-log__bar">
              <span className="dl-log__name">registro operativo</span>
              <span className="dl-log__live">
                <span className="dl-log__pulse" />
                en vivo
              </span>
            </div>
            {LOG_ROWS.map((row, i) => (
              <motion.div
                key={`${row.time}-${row.event}`}
                className="dl-log__row"
                {...(reduceMotion
                  ? {}
                  : {
                      initial: { opacity: 0, x: -10 },
                      whileInView: { opacity: 1, x: 0 },
                      viewport: { once: true, amount: 0.6 },
                      transition: { duration: 0.4, delay: 0.12 * i, ease }
                    })}
              >
                <span className="dl-log__time">{row.time}</span>
                <span className="dl-log__event">{row.event}</span>
                <span className="dl-log__status">{row.status}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.article>

      <motion.article className="dl-chapter" {...chapterMotion(reduceMotion, 0.04)}>
        <header className="dl-chapter__head">
          <span className="dl-chapter__index" aria-hidden="true">
            02
          </span>
          <h2 className="dl-chapter__title">Quién te acompaña</h2>
        </header>

        <p className="dl-chapter__body">
          La operativa de tu negocio no debería quedar en manos de un equipo que
          no conocés. Por eso acompañamos cada etapa del desarrollo:{' '}
          <strong>sin intermediarios</strong>, sin filas de espera.
        </p>
        <p className="dl-chapter__punch">Sin tickets ni solicitudes.</p>
        <p className="dl-chapter__body">
          Un <strong>grupo de WhatsApp dedicado</strong> a tu cuenta. Conversación
          fluida. Reuniones cuando haga falta.
        </p>

        <div className="dl-chat" aria-hidden="true">
          <div className="dl-chat__bar">
            <span className="dl-chat__avatar">R</span>
            <div className="dl-chat__meta">
              <p className="dl-chat__name">Tu cuenta · Runamatic</p>
              <p className="dl-chat__state">Grupo dedicado · siempre activo</p>
            </div>
            <span className="dl-chat__badge">directo</span>
          </div>
          <div className="dl-chat__thread">
            <motion.p
              className="dl-bubble dl-bubble--us"
              {...(reduceMotion
                ? {}
                : {
                    initial: { opacity: 0, y: 8 },
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true },
                    transition: { duration: 0.4, delay: 0.08, ease }
                  })}
            >
              Módulo de pedidos listo para revisar. Sin ticket: lo vemos acá.
            </motion.p>
            <motion.p
              className="dl-bubble dl-bubble--you"
              {...(reduceMotion
                ? {}
                : {
                    initial: { opacity: 0, y: 8 },
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true },
                    transition: { duration: 0.4, delay: 0.2, ease }
                  })}
            >
              Perfecto. Lo vemos mañana a las 10.
            </motion.p>
            <motion.p
              className="dl-bubble dl-bubble--us"
              {...(reduceMotion
                ? {}
                : {
                    initial: { opacity: 0, y: 8 },
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true },
                    transition: { duration: 0.4, delay: 0.32, ease }
                  })}
            >
              Reunión agendada. Seguimos en el grupo.
            </motion.p>
          </div>
        </div>
      </motion.article>

      <motion.article className="dl-chapter" {...chapterMotion(reduceMotion, 0.06)}>
        <header className="dl-chapter__head">
          <span className="dl-chapter__index" aria-hidden="true">
            03
          </span>
          <h2 className="dl-chapter__title">El proceso</h2>
        </header>

        <p className="dl-chapter__body">
          Sin sorpresas ni procesos opacos. Seis pasos, de la primera conversación
          a un <strong>sistema funcionando</strong>.
        </p>

        <ol className="dl-pipeline">
          {PROCESS_STEPS.map((step, i) => (
            <motion.li
              key={step.title}
              className="dl-pipeline__item"
              {...(reduceMotion
                ? {}
                : {
                    initial: { opacity: 0, y: 12 },
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true, amount: 0.5 },
                    transition: { duration: 0.4, delay: 0.06 * i, ease }
                  })}
            >
              <span className="dl-pipeline__n">{String(i + 1).padStart(2, '0')}</span>
              <div className="dl-pipeline__copy">
                <p className="dl-pipeline__title">{step.title}</p>
                <p className="dl-pipeline__desc">{step.desc}</p>
              </div>
            </motion.li>
          ))}
        </ol>
      </motion.article>
    </section>
  );
};

export default DevLandingStory;
